/**
 * Wide-view look controls: mouse drag / touch drag to survey the ocean, wheel or pinch
 * to zoom. Yaw is free through the full circle (wrapping at ±180 so dead-astern is
 * seamless); pitch is clamped to the band between the sky and the deck underfoot.
 *
 * Input hardening (IMPLEMENTATION_PLAN.md A8): dragging must cancel cleanly on
 * pointer-cancel, focus loss, tab-hide and orientation change, so a lifted finger or an
 * incoming notification can never leave the camera stuck mid-drag.
 */

import type { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { clamp, degToRad } from '../sim/math';

export interface LookLimits {
  /**
   * Maximum yaw either side of the default bearing, degrees. `Infinity` means free
   * rotation: yaw wraps at ±180 rather than clamping, so crossing dead-astern is
   * seamless.
   */
  yawRange: number;
  minPitch: number;
  maxPitch: number;
}

export const DEFAULT_LOOK_LIMITS: LookLimits = {
  // Was 110 — the arc the rig originally covered. Full circle on Adam's call
  // (2026-07-28): the structure now surrounds the vantage on every bearing.
  yawRange: Infinity,
  minPitch: -25,
  // Steep enough to look straight down over the platform edge at the legs and the
  // water directly below.
  maxPitch: 72,
};

/** Wrap a free yaw into [-180, 180) so a long session cannot drift the angle. */
function wrapYaw(deg: number): number {
  return ((deg + 180) % 360 + 360) % 360 - 180;
}

/** Clamp against a finite arc, or wrap when the arc is open. */
function applyYawLimit(deg: number, limits: LookLimits): number {
  return Number.isFinite(limits.yawRange)
    ? clamp(deg, -limits.yawRange, limits.yawRange)
    : wrapYaw(deg);
}

/**
 * Extra calming applied to a finger, on top of the magnification scaling below.
 *
 * A thumb is not a mouse: it pivots rather than slides, it carries the weight of the
 * hand, and it lands with a few pixels of travel it did not intend. The same
 * degrees-per-pixel that reads as precise under a mouse reads as twitchy under a thumb,
 * which is what Adam hit on the first phone build.
 */
const TOUCH_SENSITIVITY = 0.62;

/**
 * The narrowest the magnification scaling is allowed to make the controls, as a fraction
 * of the wide view's rate.
 *
 * Pure proportionality would divide by the full 8×, which turns a careful tracking pull
 * into a shove that barely moves the crosshair. This floors it so the highest zooms stay
 * usable while still being markedly finer than the wide view.
 */
const MIN_FOV_SCALE = 0.22;

/**
 * How far a second finger must travel before it is a pinch rather than furniture, pixels.
 *
 * The bug this exists for, measured on a 430x753 phone: the look path used to hand the
 * gesture to the pinch branch the instant a second pointer existed —
 * `if (pointers.size === 2) { zoom; return; }` — so *any* second contact killed the
 * camera outright. A swipe with a finger resting anywhere else on the glass moved the
 * view by exactly 0.00 degrees. That is the thumb case Adam reported: a thumb pivots
 * from the base of the hand rather than sliding on its tip, so the heel of it catches
 * the screen far more often than a fingertip does, and the swipe silently became a
 * zero-distance pinch.
 *
 * A real pinch has two fingers moving. A resting one is furniture and must not stop the
 * drag. Generous on purpose — a stationary finger still jitters several pixels against
 * the digitiser, and every pixel under this threshold is a swipe that keeps working.
 */
const PINCH_FINGER_SLOP_PX = 34;

/**
 * How far a finger must travel before a touch that began on a HUD control is taken over
 * as a look drag, pixels.
 *
 * Only applies to gestures that start on the HUD; a drag beginning on open water steers
 * from the first pixel, as it always has. So this is pure tap/drag disambiguation and it
 * wants to be well clear of an ordinary tap's wander. A thumb tap on a phone routinely
 * slides 10-15 px between touchdown and lift, which is why the first guess of 8 was too
 * small — under it, tapping HORIZON would sometimes swing the view instead of switching
 * the view. Android's own touch slop is ~8dp (24 px at 3x) and this sits just above it.
 */
const HUD_DRAG_SLOP_PX = 26;

/**
 * Controls that legitimately own a finger for the whole gesture, so the camera must
 * never steal it: the trigger (hold to fire, hold-then-slide to pick a fuse) and the
 * fuse selector it opens.
 */
const POINTER_OWNERS = '#fireButton, #fuseSelector';

export interface LookControlsOptions {
  camera: FreeCamera;
  element: HTMLElement;
  limits?: LookLimits;
  /** Degrees of camera rotation per pixel of drag, at the wide view's field of view. */
  sensitivity?: number;
  /**
   * The field of view `sensitivity` is quoted at, radians — the wide view's.
   *
   * Look rate scales with the camera's current FOV against this. Without it the rate is
   * a fixed number of degrees per pixel, so entering the scope keeps the angular speed
   * while shrinking the frame those degrees are spread across: at 8× a drag moves the
   * image eight times as far as the same drag in the wide view. That is the whole of
   * "too sensitive in scope, worse when zoomed in" — the controls were not tuned wrong,
   * they were tuned in the wrong unit.
   */
  referenceFov?: number;
  onZoom?: (delta: number) => void;
  /**
   * Enable pointer-lock look on desktop.
   *
   * Without it, look and fire both live on the left button and you cannot survey the sea
   * without discharging the gun. Locked, the mouse aims with no button held and the left
   * button means only one thing.
   */
  pointerLock?: boolean;
  /** Called when the lock is gained or lost, so the caller can rebind the fire button. */
  onPointerLockChange?: (locked: boolean) => void;
}

/**
 * The sign convention for a captured mouse, as pure math.
 *
 * Extracted so it can be unit-tested: pointer lock is not granted in every headless
 * browser, so an end-to-end test cannot reliably measure it — and this is exactly the
 * kind of rule that gets flipped by accident and feels awful without failing anything.
 *
 * A captured mouse *is* the aim: right looks right, down looks down. That is the opposite
 * of the drag gesture, where you grab the world and pull it, and the opposition is
 * deliberate.
 */
export function stepLockedLook(
  current: { yaw: number; pitch: number },
  movementX: number,
  movementY: number,
  sensitivity: number,
  invertPitch: boolean,
  limits: LookLimits,
): { yaw: number; pitch: number } {
  return {
    yaw: applyYawLimit(current.yaw + movementX * sensitivity, limits),
    pitch: clamp(
      current.pitch + movementY * sensitivity * (invertPitch ? -1 : 1),
      limits.minPitch, limits.maxPitch,
    ),
  };
}

/**
 * Degrees of rotation per pixel, for a given field of view and input device.
 *
 * Extracted and exported for the same reason `stepLockedLook` is: this is feel, it has
 * no failure mode that throws, and a regression here is the kind that ships. A test can
 * assert the relationships — scope finer than wide, 8× finer than 2×, touch calmer than
 * mouse — without needing a camera or a browser.
 *
 * Scaling by FOV is what makes the optic behave like an optic. At 8× the frame shows an
 * eighth of the arc, so a pixel of drag must be worth an eighth of the angle, or the
 * image slides eight times as fast under the same finger. Floored at `MIN_FOV_SCALE` so
 * the top of the zoom range stays draggable rather than becoming a shove.
 */
export function lookRate(
  sensitivity: number,
  cameraFov: number,
  referenceFov: number | undefined,
  touch: boolean,
): number {
  const fovScale = referenceFov
    ? Math.max(MIN_FOV_SCALE, Math.min(1, cameraFov / referenceFov))
    : 1;
  return sensitivity * fovScale * (touch ? TOUCH_SENSITIVITY : 1);
}

/**
 * Whether the left mouse button should work the trigger.
 *
 * **The mouse is a scope-only trigger.** In HORIZON the left button does nothing but
 * look; the 30 mm fires from the spacebar, in both views.
 *
 * Pure, because the rule it encodes is the one that keeps breaking. Two corrections are
 * baked in and both cost a shipped build:
 *
 *  1. The trigger used to be gated on the pointer lock alone, and a browser that refuses
 *     the lock therefore had no trigger at all. WebKit refuses it — every
 *     `requestPointerLock` answers with `pointerlockerror` — so in Safari the scope could
 *     be aimed by dragging and never fired, which is exactly "I can't fire and move at
 *     the same time" from the other side. Hence `lockUnavailable` arming it: once the
 *     browser has definitively refused, the button does both jobs at once down the optic.
 *  2. Arming it in HORIZON as well (the first cut of Adam's "I want to be able to shoot
 *     the 30 mm when on Horizon view") made the wide view unusable: dragging to look
 *     around IS a held left button, so every survey sweep emptied the magazine. Adam,
 *     2026-07-30: "it should only fire the gun on mouse left click in scope mode. In
 *     horizon mode on desktop only spacebar should fire the 30 mm." HORIZON keeps its
 *     firing — `Space` reaches it, and that is what makes this rule affordable — while
 *     the button goes back to meaning one thing out there.
 *
 * So: never in HORIZON, and in the scope only once the lock is held or refused. An
 * unlocked, not-yet-refused click is the click that TAKES the pointer; firing on it would
 * discharge the gun on the way into the optic.
 *
 * Touch is not this decision. A finger never reaches here — `attachInput` filters to
 * `pointerType === 'mouse'` — and fires from the on-screen FIRE button in both views.
 */
export function mouseTriggerArmed(
  inScope: boolean,
  locked: boolean,
  lockUnavailable: boolean,
): boolean {
  return inScope && (locked || lockUnavailable);
}

/**
 * Convert a pinch/wheel movement into a magnification multiplier.
 *
 * The input path reports many tiny distance changes while two fingers move. The old
 * mobile path discarded their size and applied a full desktop wheel jump on every one,
 * so a short pinch tore through the whole 2–8× range. Mobile now follows the real
 * distance change at 50% sensitivity; mouse-wheel behaviour stays untouched.
 */
export function zoomGestureMultiplier(
  distanceFactor: number,
  coarsePointer: boolean,
): number {
  if (distanceFactor === 1) return 1;
  if (coarsePointer) return 1 + (distanceFactor - 1) * 0.5;
  const fullStep = distanceFactor > 1 ? 0.15 : -0.13;
  return 1 + fullStep;
}

/**
 * Whether two touching fingers are pinching, or one is simply resting on the glass.
 *
 * Pure because it is the rule that broke: a resting finger used to count as a pinch and
 * silently disabled the camera for the whole gesture. Both fingers have to be doing
 * something for it to be a zoom.
 */
export function isPinchGesture(movedA: number, movedB: number): boolean {
  return Math.min(movedA, movedB) >= PINCH_FINGER_SLOP_PX;
}

/**
 * Whether a gesture that began on `target` may be taken over as a look drag, and how far
 * it must travel first.
 *
 * `null` means hands off — the trigger owns its finger for the whole gesture, and so
 * does anything outside the game surface, which is how the title screen, the pause menu
 * and the debug panels stay draggable-proof. `0` is open water: steer from the first
 * pixel, exactly as before. `HUD_DRAG_SLOP_PX` is a HUD control that can still be
 * tapped, so the camera only takes the finger once it has stopped behaving like a tap.
 */
export function dragSlopFor(
  where: { ownsPointer: boolean; inHud: boolean; onSurface: boolean },
): number | null {
  if (where.ownsPointer) return null;
  if (where.inHud) return HUD_DRAG_SLOP_PX;
  return where.onSurface ? 0 : null;
}

/** Classify an event target against the rule above. The DOM half, kept out of the pure bit. */
function locate(
  target: Element | null,
  surface: Element,
): { ownsPointer: boolean; inHud: boolean; onSurface: boolean } {
  if (!target) return { ownsPointer: false, inHud: false, onSurface: false };
  return {
    ownsPointer: target.closest(POINTER_OWNERS) !== null,
    inHud: target.closest('#hud') !== null,
    onSurface: target === surface || surface.contains(target),
  };
}

export class LookControls {
  private readonly camera: FreeCamera;
  private readonly element: HTMLElement;
  private readonly limits: LookLimits;
  private readonly sensitivity: number;
  private readonly referenceFov: number | undefined;
  private readonly onZoom: ((delta: number) => void) | undefined;

  private yaw = 0;
  private pitch = 13;
  /**
   * Active pointers over the game surface.
   *
   * `moved` is the total path length since touchdown, which is what separates a pinching
   * finger from a resting one and a drag from a tap. `slop` is how far this pointer must
   * travel before it may steer (0 on open water, more over the HUD); `steering` latches
   * once it has.
   */
  private readonly pointers = new Map<number, {
    x: number; y: number; moved: number; slop: number; steering: boolean;
  }>();
  private dragId: number | null = null;
  private pinchStart = 0;
  /** Latched once two fingers are genuinely pinching, so the look stands down. */
  private pinching = false;
  /**
   * Set when a gesture became a drag, so the click it would otherwise synthesise is
   * swallowed — otherwise dragging the view from the HORIZON tab would switch views on
   * release.
   */
  private suppressClick = false;
  private disposers: (() => void)[] = [];
  /**
   * Vertical inversion, applied to pitch only.
   *
   * Settable at runtime rather than fixed at construction, because it is toggled from
   * inside the scope mid-engagement — the whole point is that a player who discovers the
   * aim feels backwards can fix it without leaving the fight.
   */
  invertPitch = false;
  private readonly wantPointerLock: boolean;
  private readonly onPointerLockChange: ((locked: boolean) => void) | undefined;
  private locked = false;
  /** Set when a request comes back with `pointerlockerror`. */
  private lockErrored = false;
  /** Set the first time a lock is actually granted. Proof the browser can do it. */
  private everLocked = false;

  /**
   * True once we know this browser will not give us the pointer.
   *
   * Requires that we have never held the lock, because a single `pointerlockerror` is not
   * proof of anything on its own: Chromium raises one for a request made too soon after
   * the last exit, and treating that as a permanent verdict would quietly demote a browser
   * that works. A browser that has granted the lock once can do it, and this stays false
   * for the rest of the session.
   */
  get lockUnavailable(): boolean {
    return this.lockErrored && !this.everLocked;
  }

  constructor(opts: LookControlsOptions) {
    this.camera = opts.camera;
    this.element = opts.element;
    this.limits = opts.limits ?? DEFAULT_LOOK_LIMITS;
    this.sensitivity = opts.sensitivity ?? 0.12;
    this.referenceFov = opts.referenceFov;
    this.onZoom = opts.onZoom;
    this.wantPointerLock = opts.pointerLock ?? false;
    this.onPointerLockChange = opts.onPointerLockChange;
    this.attach();
    this.apply();
  }

  private rate(touch: boolean): number {
    return lookRate(this.sensitivity, this.camera.fov, this.referenceFov, touch);
  }

  private on<K extends keyof HTMLElementEventMap>(
    target: HTMLElement | Window | Document,
    type: K | string,
    handler: (ev: never) => void,
    options?: AddEventListenerOptions,
  ): void {
    const h = handler as EventListener;
    target.addEventListener(type, h, options);
    this.disposers.push(() => target.removeEventListener(type, h, options));
  }

  /** True while the pointer is locked to the canvas. */
  get isLocked(): boolean {
    return this.locked;
  }

  /**
   * Ask for the pointer lock.
   *
   * Browsers only grant this from a user gesture and reject otherwise — and in modern
   * Chromium the rejection is a promise, so an uncaught call surfaces as an unhandled
   * rejection and trips the zero-console-error gate. Lock is an enhancement: if it is
   * refused, drag-look still works, so the failure is swallowed deliberately.
   */
  requestLock(): void {
    if (!this.wantPointerLock) return;
    if (document.pointerLockElement === this.element) return;
    try {
      const result = (this.element as HTMLElement & {
        requestPointerLock?: () => Promise<void> | void;
      }).requestPointerLock?.();
      if (result && typeof (result as Promise<void>).catch === 'function') {
        (result as Promise<void>).catch(() => { /* refused; drag-look remains */ });
      }
    } catch {
      /* refused synchronously; drag-look remains */
    }
  }

  private attach(): void {
    if (this.wantPointerLock) {
      this.on(document, 'pointerlockchange', () => {
        this.locked = document.pointerLockElement === this.element;
        if (this.locked) { this.everLocked = true; this.lockErrored = false; }
        this.onPointerLockChange?.(this.locked);
      });
      /*
       * A refusal is information, not just a failure to swallow.
       *
       * It is the difference between "the player has not clicked to take the pointer yet"
       * and "this browser is never going to hand it over", and the trigger has to know
       * which — see `mouseTriggerArmed`. Deliberately *not* reported through
       * `onPointerLockChange`: that channel means "the lock was released", and the game
       * answers it by dropping the trigger and starting the Escape grace window. A refusal
       * released nothing, and firing it down that path would cut a burst short and eat the
       * next Escape.
       */
      this.on(document, 'pointerlockerror', () => {
        this.lockErrored = true;
      });
      /*
       * Locked look: raw movement deltas, no button required.
       *
       * The sign convention here is the opposite of the drag path below, and that is not
       * an inconsistency — the two gestures mean different things. Dragging grabs the
       * world and pulls it, so the view goes the other way. A captured mouse *is* the
       * aim, so moving it right must look right and moving it down must look down. This
       * originally reused the drag formula, which made both axes feel backwards inside
       * the scope.
       */
      this.on(window, 'mousemove', (ev: MouseEvent) => {
        if (!this.locked) return;
        const next = stepLockedLook(
          { yaw: this.yaw, pitch: this.pitch },
          ev.movementX, ev.movementY,
          this.rate(false), this.invertPitch, this.limits,
        );
        this.yaw = next.yaw;
        this.pitch = next.pitch;
        this.apply();
      });
    }

    /*
     * Listening on the window, in the capture phase, rather than on the canvas.
     *
     * The canvas only ever hears about touches that land on bare water, and on a phone a
     * great deal of the sea is not bare: a contact marker is a 120x133 px box (measured)
     * floating over the water with `pointer-events: auto`, and the bottom 110 px is a
     * full-width strip of view tabs and utility buttons. A swipe starting on any of them
     * used to reach nothing at all — the marker swallowed it, and the tabs fire on
     * `click`, which never comes if the finger travels. Capture-phase window listeners
     * see every touch wherever it lands, and `dragSlopFor` decides what may be taken
     * over: the trigger keeps its finger, the HUD yields one that is clearly dragging,
     * and open water steers from the first pixel exactly as before.
     */
    /*
     * Forget one pointer, however its gesture ended.
     *
     * Shared by `pointerup`, `pointercancel` and the no-buttons guard below, because a
     * pointer stranded in the map is the same defect whichever release went missing.
     */
    const release = (pointerId: number): void => {
      this.pointers.delete(pointerId);
      if (pointerId === this.dragId) {
        this.dragId = null;
        /*
         * Hand the drag to whatever finger is still down, rather than dropping the
         * gesture. Lifting the first of two fingers used to leave the camera inert until
         * everything came off the glass.
         */
        const next = this.pointers.keys().next();
        if (!next.done) this.dragId = next.value;
      }
      if (this.pointers.size < 2) { this.pinchStart = 0; this.pinching = false; }
    };

    this.on(window, 'pointerdown', (ev: PointerEvent) => {
      // While locked, dragging is meaningless — the mouse is already the aim.
      if (this.locked) return;
      /*
       * Only the left button steers. A right or middle press opens a context menu or an
       * autoscroll widget that swallows the matching `pointerup`, which stranded the
       * pointer here for the rest of the session.
       */
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;
      const slop = dragSlopFor(
        locate(ev.target instanceof Element ? ev.target : null, this.element),
      );
      if (slop === null) return;
      // A fresh gesture clears any suppression the last one armed but never spent: a
      // touch drag does not always synthesise a click, and a flag left standing would
      // eat the next legitimate tap.
      if (this.pointers.size === 0) this.suppressClick = false;
      this.pointers.set(ev.pointerId, {
        x: ev.clientX, y: ev.clientY, moved: 0, slop, steering: slop === 0,
      });
      if (this.dragId === null) this.dragId = ev.pointerId;
      if (this.pointers.size === 2) this.pinchStart = this.pinchDistance();
    }, { capture: true });

    this.on(window, 'pointermove', (ev: PointerEvent) => {
      if (this.locked) return;
      const prev = this.pointers.get(ev.pointerId);
      if (!prev) return;
      /*
       * A mouse holding nothing down is not dragging — trust `buttons`, not a
       * `pointerup` that may never arrive.
       *
       * Adam, 2026-07-30: "the head is like..loose — not fixed left and right looking."
       * The release is genuinely not guaranteed: let go while the cursor is over browser
       * chrome or past the edge of the screen and the page never sees `pointerup`, and
       * `blur` does not fire either because the window kept focus. The entry then sits
       * in `pointers` forever and the camera follows a mouse with no button held, so
       * merely reaching for the HUD swings the view. Free 360° yaw makes it routine on
       * desktop: a long spin runs the cursor off-screen every time.
       *
       * Every move event carries the live button state, so it is the one source that
       * cannot go stale. Mouse only — a finger reports `buttons === 0` while genuinely
       * dragging, and touch has `pointercancel` for the cases this covers.
       */
      if (ev.pointerType === 'mouse' && ev.buttons === 0) { release(ev.pointerId); return; }
      const dx = ev.clientX - prev.x;
      const dy = ev.clientY - prev.y;
      const moved = prev.moved + Math.hypot(dx, dy);
      /*
       * A finger that has travelled past its slop starts steering, and does so from
       * *here* rather than from where it touched down — carrying the accumulated slop
       * into the first frame would snap the view sideways the instant a tap became a
       * drag.
       */
      const steering = prev.steering || moved >= prev.slop;
      this.pointers.set(ev.pointerId, {
        x: ev.clientX, y: ev.clientY, moved, slop: prev.slop, steering,
      });
      if (steering && !prev.steering) this.suppressClick = true;

      /*
       * Two fingers are only a pinch when both of them are moving. This used to trigger
       * on the mere presence of a second pointer, which meant a finger resting anywhere
       * on the glass disabled the camera completely for as long as it stayed down.
       */
      if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        if (a && b && isPinchGesture(a.moved, b.moved)) {
          if (!this.pinching) { this.pinching = true; this.pinchStart = this.pinchDistance(); }
          const d = this.pinchDistance();
          if (this.pinchStart > 0 && this.onZoom) this.onZoom(d / this.pinchStart);
          this.pinchStart = d;
          return;
        }
      }
      if (this.pinching) return;
      /*
       * The drag follows the finger that is actually travelling, not the one that landed
       * first.
       *
       * This is the other half of the resting-finger bug, and the half that survived the
       * first fix. A hand holding a phone puts something on the glass *before* the thumb
       * starts moving — so the stationary contact won the drag by seniority and the
       * thumb, not being `dragId`, was discarded on every move. Measured: 0.00 degrees
       * for the whole swipe. Whoever has covered the most ground owns the camera.
       */
      if (ev.pointerId !== this.dragId) {
        const holder = this.dragId === null ? null : this.pointers.get(this.dragId);
        if (holder && holder.moved >= moved) return;
        this.dragId = ev.pointerId;
      }
      if (!steering || !prev.steering) return;

      const rate = this.rate(ev.pointerType === 'touch');
      this.yaw = applyYawLimit(this.yaw - dx * rate, this.limits);
      this.pitch = clamp(
        this.pitch - dy * rate * (this.invertPitch ? -1 : 1),
        this.limits.minPitch, this.limits.maxPitch,
      );
      this.apply();
    }, { capture: true });

    const end = (ev: PointerEvent): void => release(ev.pointerId);
    this.on(window, 'pointerup', end, { capture: true });
    // pointercancel fires when the OS steals the gesture (notification, system swipe).
    this.on(window, 'pointercancel', end, { capture: true });

    /*
     * A drag must not also press the control it started on. Swallowed in the capture
     * phase so the HUD's own `click` handlers never run — dragging the view up from the
     * HORIZON tab should move the view, not switch to SCOPE.
     */
    this.on(window, 'click', (ev: MouseEvent) => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      ev.stopPropagation();
      ev.preventDefault();
    }, { capture: true });

    // Any loss of focus or visibility drops every active pointer.
    const cancelAll = (): void => {
      this.pointers.clear();
      this.dragId = null;
      this.pinchStart = 0;
      this.pinching = false;
      this.suppressClick = false;
    };
    this.on(window, 'blur', cancelAll);
    this.on(document, 'visibilitychange', cancelAll);
    this.on(window, 'orientationchange', cancelAll);

    this.on(this.element, 'wheel', (ev: WheelEvent) => {
      ev.preventDefault();
      if (this.onZoom) this.onZoom(ev.deltaY > 0 ? 0.9 : 1.1);
    }, { passive: false });

    // Suppress the browser's own gestures over the game surface.
    this.on(this.element, 'contextmenu', (ev: Event) => ev.preventDefault());
    this.on(this.element, 'gesturestart', (ev: Event) => ev.preventDefault());
    this.on(this.element, 'dblclick', (ev: Event) => ev.preventDefault());
  }

  private pinchDistance(): number {
    const pts = Array.from(this.pointers.values());
    if (pts.length < 2) return 0;
    const a = pts[0] as { x: number; y: number };
    const b = pts[1] as { x: number; y: number };
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private apply(): void {
    this.camera.rotation.set(degToRad(this.pitch), degToRad(this.yaw), 0);
  }

  setOrientation(yawDeg: number, pitchDeg: number): void {
    this.yaw = applyYawLimit(yawDeg, this.limits);
    this.pitch = clamp(pitchDeg, this.limits.minPitch, this.limits.maxPitch);
    this.apply();
  }

  get orientation(): { yaw: number; pitch: number } {
    return { yaw: this.yaw, pitch: this.pitch };
  }

  /** True while the user is actively dragging — used to suppress idle camera drift. */
  get isDragging(): boolean {
    return this.dragId !== null;
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.pointers.clear();
  }
}
